/**
 * @file   src\Pages\CWOSignupBankInformation.tsx
 * @brief  Carwash owner bank information page.
 * @date   Nov, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useEffect, useOutletContext, Container, Row, Col, useRef } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWInput from '../components/CWInput';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import { ACCOUNT_NAME_MAX_LENGTH, ACCOUNT_NUMBER_MAX_LENGTH, ROUTING_NUMBER_MAX_LENGTH } from '../utils/constants';
import CWSelect from '../components/CWSelect';
import { AccountHolderType } from '../utils/enums';
import { cwoaddACHBankAccountSchema } from '../validations/cwoaddCarwashSchema';
import * as Yup from 'yup'

interface CWOSignupBankInformationProps {
    embedded?: boolean;
    onComplete?: () => void;
    locationId?: number;
    onValidate?: (validateFunc: () => Promise<boolean>) => void;
    onDataChange?: (data: any) => void;
    m_formData?: any;
}

const CWOSignupBankInformation = ({ embedded = false, onValidate, onDataChange, m_formData }: CWOSignupBankInformationProps) => {
    const { updateData } = useOutletContext<AppContextType>();
    const formRef = useRef<HTMLFormElement>(null);

    const [formData, setFormData] = useState({
        accountholdername: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: '',
        accountholdertype: 1,
    })

    const [errors, setErrors] = useState({
        accountholdername: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: '',
        accountholdertype: '',
    });

    useEffect(() => {
        if (!embedded) {
            updateData({ heading: "Bank Information", url: `/${pageURLs.manage_carwash}`, showBackButton: false, showMainMenu: false, activeLink: "manage-car-wash" });
        }
    }, [embedded])

    const accountholderOptions = [
        { label: 'Select', value: 0 },
        ...Object.keys(AccountHolderType).filter(key => !isNaN(Number(key))).map(key => ({
            label: AccountHolderType[key as keyof typeof AccountHolderType],
            value: Number(key)
        }))
    ];

    const validateForm = async (): Promise<boolean> => {
        try {
            await cwoaddACHBankAccountSchema.validate(formData, { abortEarly: false });
            setErrors({
                accountholdername: '',
                accountnumber: '',
                retypeaccountnumber: '',
                routingnumber: '',
                accountholdertype: '',
            });
            return true;
        } catch (error) {
            const newErrors: any = {};
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }

            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
            return false;
        }
    };

    const updateFormData = (updatedData: any) => {
        setFormData(updatedData);
        onDataChange && onDataChange(updatedData);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target
        const updatedData = {
            ...formData,
            [name]: value,
        };
        updateFormData(updatedData);

        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
    }

    const handlepaymentSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';
        const updatedData = {
            ...formData,
            [name]: value,
        };
        updateFormData(updatedData);

        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
    };

    const onpaymentinfoInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.name === "routingnumber") {
            const routingnumber_value = event.target.value;
            if (!/^\d*$/.test(routingnumber_value)) {
                event.target.value = routingnumber_value.replace(/\D/g, '');
            }
        }

        if (event.target.name === "accountnumber") {
            const accountnumber_value = event.target.value;
            if (!/^\d*$/.test(accountnumber_value)) {
                event.target.value = accountnumber_value.replace(/\D/g, '');
            }
        }

        if (event.target.name === "retypeaccountnumber") {
            const retypeaccountnumber_value = event.target.value;
            if (!/^\d*$/.test(retypeaccountnumber_value)) {
                event.target.value = retypeaccountnumber_value.replace(/\D/g, '');
            }
        }

        const { name, value } = event.target;
        const updatedData = {
            ...formData,
            [name]: value,
        };
        updateFormData(updatedData);

        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
    };

    useEffect(() => {
        onValidate && onValidate(validateForm);
    }, [formData, onValidate]);

    useEffect(() => {
        if (m_formData != null) {
            setFormData(m_formData ? m_formData : formData);
        }
    }, [m_formData]);

    return (
        <div className='manage-subscription-container'>
            <Container>
                <div className='cw-form-outer-wrap'>
                    <form ref={formRef}>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">
                                <h5 className='mt-3'>Your Bank information is required for processing monthly payments via ACH to carwashapp.com.</h5>
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="accountholdertype"
                                            name="accountholdertype"
                                            options={accountholderOptions}
                                            label="Select Account Holder Type"
                                            errorMessage={errors.accountholdertype && errors.accountholdertype}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            values={accountholderOptions.filter(option => option.value === formData.accountholdertype)}
                                            onChange={(selectedOptions) => handlepaymentSelectChange(selectedOptions[0], 'accountholdertype')}
                                            placeholder="Select Account Holder Type"
                                            multi={false}
                                            searchable={false}
                                            isMandatory
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="accountholdername"
                                            name="accountholdername"
                                            type="text"
                                            label="Account Name"
                                            value={formData.accountholdername}
                                            onChange={handleInputChange}
                                            errorMessage={errors.accountholdername}
                                            maxLength={ACCOUNT_NAME_MAX_LENGTH}
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="routingnumber"
                                            name="routingnumber"
                                            type="text"
                                            label="Routing Number"
                                            value={formData.routingnumber}
                                            onChange={onpaymentinfoInputChange}
                                            errorMessage={errors.routingnumber}
                                            maxLength={ROUTING_NUMBER_MAX_LENGTH}
                                            autoComplete="off"
                                            isMandatory
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="accountnumber"
                                            name="accountnumber"
                                            type="text"
                                            label="Account Number"
                                            value={formData.accountnumber}
                                            onChange={onpaymentinfoInputChange}
                                            errorMessage={errors.accountnumber}
                                            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                            autoComplete="off"
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="retypeaccountnumber"
                                            name="retypeaccountnumber"
                                            type="text"
                                            label="Re-Type Account Number"
                                            value={formData.retypeaccountnumber}
                                            onChange={onpaymentinfoInputChange}
                                            errorMessage={errors.retypeaccountnumber}
                                            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                            autoComplete="off"
                                            isMandatory
                                            onCopy={(e) => e.preventDefault()}
                                            onCut={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </form>
                </div>
            </Container>
        </div>
    )
}

export default CWOSignupBankInformation;
